import { Redirect, Switch, useRouteMatch } from 'react-router-dom';
import Content from 'assets/js/components/Content';
import { BrandingSettings } from 'assets/js/pages/engage/BrandingSettings';
import DomainSettings from 'assets/js/pages/engage/DomainSettings';
import EngageKeywords from 'assets/js/pages/engage/KeywordSettings';
import { MergeFields } from 'assets/js/pages/engage/MergeFields';
import EngageNumbers from 'assets/js/pages/engage/NumberSettings';
import SenderSettings from 'assets/js/pages/engage/SenderSettings';
import { AddDomainDrawer } from 'assets/js/pages/engage/components/DomainSettings/AddDomainDrawer';
import { AddSenderDrawer } from 'assets/js/pages/engage/components/DomainSettings/AddSenderDrawer';
import { EditDomainDrawer } from 'assets/js/pages/engage/components/DomainSettings/EditDomainDrawer';
import { EditSenderDrawer } from 'assets/js/pages/engage/components/DomainSettings/EditSenderDrawer';
import { useEngageConfig } from 'assets/js/pages/engage/useEngageConfiguration';
import { AddressFormDrawer } from 'components/address-form/AddressFormDrawer';
import { EngageAddressForm } from 'components/address-form/EngageAddressForm';
import { DrawerRoute } from 'components/drawer-route';
import * as NavMenu from 'components/nav-menu';
import { Route } from 'components/router/Route';
import * as SubNav from 'components/sub-nav';
import { EngageSubNavTabs } from 'components/sub-nav/EngageSubNavTabs';
import { TestIds } from 'constants/tests';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';

const DomainsRouter = () => {
    const match = useRouteMatch();

    return (
        <>
            <DrawerRoute path={`${match.path}/new`} component={AddDomainDrawer} />
            <DrawerRoute path={`${match.path}/:domain_id/edit`} component={EditDomainDrawer} />
            <Switch>
                <Route path={match.path} component={DomainSettings} />
            </Switch>
        </>
    );
};

const SendersRouter = () => {
    const match = useRouteMatch();

    return (
        <>
            <DrawerRoute path={`${match.path}/new`} component={AddSenderDrawer} />
            <DrawerRoute path={`${match.path}/:sender_id/edit`} component={EditSenderDrawer} />
            <Switch>
                <Route path={match.path} component={SenderSettings} />
            </Switch>
        </>
    );
};

const SETTINGS_ROUTES = {
    domains: { path: 'domains', label: 'Email Domains' },
    senders: { path: 'senders', label: 'Email Senders' },
    smsKeywords: { path: 'sms-keywords', label: 'SMS Keywords' },
    sms: { path: 'sms', label: 'SMS Numbers' },
    branding: { path: 'branding', label: 'Branding' },
    addresses: { path: 'addresses', label: 'Addresses' },
    mergeFields: { path: 'merge-fields', label: 'Merge Fields' },
} as const;

const DEFAULT_ROUTE = SETTINGS_ROUTES['domains'];

const EngageSettingsRouter = () => {
    const match = useRouteMatch();
    const { hasSMSKeywords } = useEngageConfig();
    const { hasTextingAccess } = useCurrentAccount();

    return (
        <Content className="flex flex-col gap-4">
            <div className="flex flex-col gap-4">
                <SubNav.Root>
                    <SubNav.Header>
                        <SubNav.Title>Engage</SubNav.Title>
                    </SubNav.Header>
                    <EngageSubNavTabs />
                </SubNav.Root>
            </div>
            <div className="grid grid-cols-12 gap-4">
                <NavMenu.Root
                    aria-label="Engage navigation"
                    orientation="vertical"
                    className="col-span-full lg:col-span-3"
                >
                    <NavMenu.Item to={`${match.url}/${SETTINGS_ROUTES.domains.path}`}>
                        {SETTINGS_ROUTES.domains.label}
                    </NavMenu.Item>

                    <NavMenu.Item
                        to={`${match.url}/${SETTINGS_ROUTES.senders.path}`}
                        data-testid={TestIds.Engage.Settings.EngageSenders}
                    >
                        {SETTINGS_ROUTES.senders.label}
                    </NavMenu.Item>

                    {hasTextingAccess && (
                        <>
                            <NavMenu.Item
                                to={`${match.url}/${SETTINGS_ROUTES.sms.path}`}
                                data-testid={TestIds.Engage.Settings.SmsNumbers}
                            >
                                {SETTINGS_ROUTES.sms.label}
                            </NavMenu.Item>

                            {hasSMSKeywords && (
                                <NavMenu.Item
                                    to={`${match.url}/${SETTINGS_ROUTES.smsKeywords.path}`}
                                    data-testid={TestIds.Engage.Settings.SmsKeywords}
                                >
                                    {SETTINGS_ROUTES.smsKeywords.label}
                                </NavMenu.Item>
                            )}
                        </>
                    )}

                    <NavMenu.Item
                        to={`${match.url}/${SETTINGS_ROUTES.branding.path}`}
                        data-testid={TestIds.Engage.Settings.Branding}
                    >
                        {SETTINGS_ROUTES.branding.label}
                    </NavMenu.Item>

                    <NavMenu.Item
                        to={`${match.url}/${SETTINGS_ROUTES.addresses.path}`}
                        data-testid={TestIds.Engage.Settings.Addresses}
                    >
                        {SETTINGS_ROUTES.addresses.label}
                    </NavMenu.Item>

                    <NavMenu.Item
                        to={`${match.url}/${SETTINGS_ROUTES.mergeFields.path}`}
                        data-testid={TestIds.Engage.Settings.MergeFields}
                    >
                        {SETTINGS_ROUTES.mergeFields.label}
                    </NavMenu.Item>
                </NavMenu.Root>
                <div className="col-span-full lg:col-span-9">
                    <Switch>
                        <Route
                            path={`${match.path}/${SETTINGS_ROUTES.domains.path}`}
                            component={DomainsRouter}
                        />
                        <Route exact component={EngageNumbers} path={`${match.path}/sms`} />
                        <Route
                            exact
                            component={EngageKeywords}
                            path={`${match.path}/${SETTINGS_ROUTES.smsKeywords.path}`}
                        />
                        <Route
                            path={`${match.path}/${SETTINGS_ROUTES.senders.path}`}
                            component={SendersRouter}
                        />
                        <Route
                            exact
                            component={BrandingSettings}
                            path={`${match.path}/${SETTINGS_ROUTES.branding.path}`}
                        />

                        <Route path={`${match.path}/${SETTINGS_ROUTES.addresses.path}`}>
                            {({ match }) => (
                                <>
                                    <DrawerRoute
                                        path={[
                                            `${match?.path}/new`,
                                            `${match?.path}/:addressId/edit`,
                                        ]}
                                        exact
                                    >
                                        <AddressFormDrawer />
                                    </DrawerRoute>
                                    <DrawerRoute
                                        path={[
                                            `${match?.path}/new/mailing`,
                                            `${match?.path}/:addressId/edit/mailing`,
                                        ]}
                                        exact
                                    >
                                        <AddressFormDrawer isMailingAddress={true} />
                                    </DrawerRoute>
                                    <Switch>
                                        <Route path={match?.path} component={EngageAddressForm} />
                                    </Switch>
                                </>
                            )}
                        </Route>
                        <Route
                            exact
                            component={MergeFields}
                            path={`${match.path}/${SETTINGS_ROUTES.mergeFields.path}`}
                        />
                        <Redirect to={`${match.path}/${DEFAULT_ROUTE.path}`} />
                    </Switch>
                </div>
            </div>
        </Content>
    );
};

export { EngageSettingsRouter };
